import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../Components/Navbar/sidenav.tsx';
import Header from '../../Components/Header/header.tsx';
import Heading from '../../Components/Heading/heading.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import Newpagination from '../../Components/Pagination/pagination.tsx';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { SelectChangeEvent } from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { getInstitutions, deleteInstitutions } from './action.ts';
import { Button, TextField, Box, Typography, useTheme, useMediaQuery, Card, CardContent, InputAdornment } from '@mui/material';
import QueryWidget from '../../Components/QueryWidget/query-widget.tsx';
import '../../Components/Button/button-style.scss';
import AddInstitutionModal from './add-institution-modal.tsx'; 
import EditInstitutionModal from './edit-institution-modal.tsx';
import { toast } from 'react-toastify'; 
import ConfirmationPopup from '../../Components/ConfirmationModal/confirmation-modal.tsx';

interface InstitutionsProps {}

const Institutions: React.FC<InstitutionsProps> = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [pageSize, setPageSize] = useState<number>(10);
    const [page, setPage] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false); 
    const [selectedInstitution, setSelectedInstitution] = useState<any>(null); 
    const [totalRecords, setTotalRecords] = useState<number>(0); 
    const [institutionList, setInstitutionList] = useState<any[]>([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [institutionToDelete, setInstitutionToDelete] = useState<any>(null);

    const showEntries = [
        { id: '10', label: '10' },
        { id: '20', label: '20' },
        { id: '50', label: '50' },
        { id: '100', label: '100' },
    ];

    const fetchInstitutions = () => {
        getInstitutions(
            pageSize,
            page,
            (response: any) => {
                const data = response?.message?.institutions_list || response?.data || [];
                const total = response?.message?.total_count || response?.total_records || 0;
                
                const formattedData = data.map((item: any, index: number) => ({
                    ...item,
                    sl_no: page * pageSize + index + 1,
                }));
                
                setInstitutionList(formattedData);
                setTotalRecords(total);
            },
            (error: any) => {
                console.error("Failed to fetch institutions:", error);
                setInstitutionList([]);
                setTotalRecords(0);
            }
        );
    };

    useEffect(() => {
        fetchInstitutions();
    }, [page, pageSize]);

    const updateField = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        if (!isNaN(Number(value))) {
            setPageSize(Number(value));
            setPage(0);
        }
    };

    const handlePageClick = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage - 1);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);  
    };

    const handleSearchClick = () => {
        setPage(0);
        fetchInstitutions();
    };

    const handleClickOpen = () => {
        setIsAddModalOpen(true);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        fetchInstitutions(); 
    };

    const handleEditClick = (row: any) => {
        setSelectedInstitution(row);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedInstitution(null);
        fetchInstitutions(); 
    };

    const handleDeleteClick = (row: any) => {
        setInstitutionToDelete(row);
        setIsConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        setIsConfirmOpen(false);
        setInstitutionToDelete(null);
    };

    const handleConfirm = () => {
        if (institutionToDelete) {
            deleteInstitutions(
                institutionToDelete.id,
                (response: any) => {
                    toast.success(response?.message || "Institution deleted successfully");
                    fetchInstitutions();
                    handleCloseConfirm();
                },
                (error: any) => {
                    toast.error(error?.message || "Failed to delete institution");
                    handleCloseConfirm();
                }
            );
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!isMobile) return;
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            const maxPage = Math.ceil(totalRecords / pageSize) - 1;
            if (page < maxPage) {
                setPage(prev => prev + 1);
            }
        }
    };

    const columns: GridColDef[] = [
        { field: 'sl_no', headerName: 'Sl No', width: 80, headerAlign: 'center', align: 'center' },
        { field: 'institution_name', headerName: 'Institution Name', flex: 1, minWidth: 200, headerAlign: 'center' },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 140, 
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            gap: '8px',
                        }}
                    >
                        <IconButton color="primary" onClick={() => handleEditClick(params.row)}>
                            <EditIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDeleteClick(params.row)}>
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                );
            },
        }
    ];

    const MobileInstitutionList = ({ rowData }: any) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', p: 1, pb: '90px' }}>
            {rowData.map((row: any) => (
                <Card 
                    key={row.id} 
                    sx={{ 
                        borderRadius: '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        overflow: 'visible',
                    }}>
                    <CardContent sx={{ pb: '12px !important', paddingTop:'10px !important' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {row.sl_no}. {row.institution_name}
                                </Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                                <IconButton size="small" color="primary" onClick={() => handleEditClick(row)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    return (
        <>   
            <Header/>
            <div style={{ marginTop: "70px" }}>
                <Heading
                    title="Institutions"
                    page='institutions'
                    closeIcon="false"
                    leftContent={
                        <>
                            <Box display="flex" alignItems="center">
                                <Typography style={{ marginRight: '5px' }}>Show Entries:</Typography>
                                <Dropdown
                                    options={showEntries}
                                    selectedValue={pageSize.toString()}
                                    handleChange={updateField}
                                    width="90px"
                                    height="40px"
                                />
                            </Box>
                        </>
                    }
                    rightContent={
                        <>
                            <Box display="flex" alignItems="center" gap="10px">
                                <TextField
                                    className="styled-textfield"
                                    variant="outlined"
                                    size="small"
                                    value={searchTerm}
                                    onChange={handleInputChange}
                                    placeholder="Search Institutions"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearchClick();
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleSearchClick} edge="end">
                                                    <SearchIcon sx={{ color: 'black' }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ marginTop: { xs: '10px', sm: '0px', md: '10px', lg: '0px' } }}>
                                <Button className="custom-button" variant="contained" onClick={handleClickOpen}>
                                    Add Institution
                                </Button>
                            </Box>
                        </>
                    }
                />
            </div>
            
            <Box 
                className="table-container custom-data-grid" 
                onScroll={handleScroll}
                sx={{ 
                    background: isMobile ? 'linear-gradient(45deg, black, rgb(237, 27, 27))' : 'none',
                    marginLeft: { xs: '0px', sm: '180px' },
                    marginTop: '10px', 
                    marginRight: '40px', 
                    position: 'relative', 
                    height: isMobile ? 'calc(100vh - 200px)' : '500px', 
                    overflowY: 'auto'
                }}
            >
                {isMobile ? (
                    <MobileInstitutionList rowData={institutionList} />
                ) : (
                    <DataGrid
                        rows={institutionList}
                        columns={columns}
                        sx={{ flex: 1, minWidth: 0, '& .MuiDataGrid-cell:focus': { outline: 'none' } }}
                        getRowHeight={() => 'auto'}
                        rowCount={totalRecords}
                        autoHeight={false}
                        disableRowSelectionOnClick
                        disableColumnResize
                        hideFooterPagination
                        classes={{ columnHeader: 'red-header' }}
                    />
                )}
            </Box>

            {!isMobile && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}> 
                    <Newpagination
                        rows={totalRecords}
                        pageSize={pageSize}
                        page={page}
                        handlePageChange={handlePageClick}
                    />
                </div>
            )}

            <NavBar selected='Institutions' />
            <QueryWidget />

            <AddInstitutionModal 
                open={isAddModalOpen} 
                onClose={handleCloseAddModal} 
            />

            <EditInstitutionModal 
                open={isEditModalOpen} 
                onClose={handleCloseEditModal} 
                institution={selectedInstitution}
            />
            
            <ConfirmationPopup 
                open={isConfirmOpen} 
                onClose={handleCloseConfirm} 
                onConfirm={handleConfirm} 
                title="Delete Institution" 
                message={`Are you sure you want to delete "${institutionToDelete?.institution_name}"?`} 
            />
            
        </>
    );
};

export default Institutions;